import React, { useState } from 'react';
import {
  Button,
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  Alert,
  Chip,
  Link,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Container,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import VerifiedIcon from '@mui/icons-material/Verified';
import SecurityIcon from '@mui/icons-material/Security';
import LockIcon from '@mui/icons-material/Lock';
import VisibilityIcon from '@mui/icons-material/Visibility';
import LinkIcon from '@mui/icons-material/Link';
import { colors, spacing, componentStyles } from '../styles/designSystem';

const useStyles = makeStyles((theme) => ({
  container: {
    ...componentStyles.sectionContainer,
    background: colors.gradients.hero,
    position: 'relative',
    overflow: 'hidden',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'radial-gradient(circle at 20% 80%, rgba(230, 0, 122, 0.1) 0%, transparent 50%)',
      pointerEvents: 'none',
    },
  },
  
  contentContainer: {
    ...componentStyles.contentContainer,
    position: 'relative',
    zIndex: 2,
  },
  
  card: {
    ...componentStyles.glassCard,
    height: '100%',
    padding: spacing.xl,
    marginBottom: spacing.lg,
  },
  
  successCard: {
    ...componentStyles.glassCard,
    height: '100%',
    padding: spacing.xl,
    marginBottom: spacing.lg,
    border: `1px solid ${colors.success.main}`,
  },
  
  warningCard: {
    ...componentStyles.glassCard,
    height: '100%',
    padding: spacing.xl,
    marginBottom: spacing.lg,
    border: `1px solid ${colors.warning.main}`,
  },
  
  button: {
    ...componentStyles.button.primary,
    margin: spacing.sm,
  },
  chip: {
    backgroundColor: colors.success.main,
    color: colors.text.primary,
    margin: spacing.xs,
  },
  warningChip: {
    backgroundColor: colors.warning.main,
    color: colors.text.primary,
    margin: spacing.xs,
  },
  table: {
    backgroundColor: colors.background.tertiary,
    '& .MuiTableCell-root': {
      color: colors.text.primary,
      borderColor: colors.border.neutral,
    },
  },
  accordion: {
    backgroundColor: colors.background.card,
    color: colors.text.primary,
    '&:before': {
      display: 'none',
    },
  },
  accordionSummary: {
    '& .MuiAccordionSummary-content': {
      color: colors.text.primary,
    },
  },
}));

const AuditTransparency = () => {
  const classes = useStyles();
  const [auditData] = useState({
    contractVerified: true,
    liquidityLocked: true,
    teamTokensLocked: true,
    auditCompleted: false,
    kycCompleted: false,
    lastUpdated: new Date().toISOString(),
  });

  const [liquidityLocks] = useState([
    {
      platform: 'Raydium',
      amount: '300,000,000 LLAMA',
      lockDuration: '1 year',
      unlockDate: '2025-01-15',
      status: 'Locked',
    },
    {
      platform: 'Orca',
      amount: '100,000,000 LLAMA',
      lockDuration: '6 months',
      unlockDate: '2024-07-15',
      status: 'Locked',
    },
  ]);

  const [teamWallets] = useState([
    {
      wallet: '5ybhspwr2nPH4zTXq95QdE4CuoxmasDuJ1d1h74hmehb',
      purpose: 'Team Development',
      tokens: '50,000,000 LLAMA',
      lockDuration: '6 months',
      unlockDate: '2024-07-15',
    },
    {
      wallet: 'FYHr6YAACyTPuALnCTcgi8Pf1odXS21UzLBX1vNskSUQ',
      purpose: 'Marketing',
      tokens: '30,000,000 LLAMA',
      lockDuration: '3 months',
      unlockDate: '2024-04-15',
    },
  ]);

  const contractLinks = {
    solanaExplorer: 'https://explorer.solana.com/address/5ybhspwr2nPH4zTXq95QdE4CuoxmasDuJ1d1h74hmehb',
    raydium: 'https://raydium.io/swap/?inputMint=sol&outputMint=5ybhspwr2nPH4zTXq95QdE4CuoxmasDuJ1d1h74hmehb',
    birdeye: 'https://birdeye.so/token/5ybhspwr2nPH4zTXq95QdE4CuoxmasDuJ1d1h74hmehb',
    dexscreener: 'https://dexscreener.com/solana/5ybhspwr2nPH4zTXq95QdE4CuoxmasDuJ1d1h74hmehb',
  };

  const getStatusChip = (status, isWarning = false) => (
    <Chip
      label={status}
      className={isWarning ? classes.warningChip : classes.chip}
      icon={status === 'Verified' || status === 'Locked' ? <VerifiedIcon /> : <SecurityIcon />}
    />
  );

  return (
    <Box className={classes.container}>
      <Container className={classes.contentContainer}>
        <Typography variant="h4" gutterBottom color="primary">
          Security & Transparency
        </Typography>
      
      <Typography variant="body1" paragraph>
        We believe in complete transparency. All contract information, liquidity locks, and team wallets are publicly verifiable.
      </Typography>

      {/* Contract Verification Status */}
      <Card className={classes.successCard}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Contract Verification Status
          </Typography>
          
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Box display="flex" alignItems="center" marginBottom={2}>
                <VerifiedIcon color="primary" style={{ marginRight: 8 }} />
                <Typography variant="body1">
                  Contract Verified on Solana Explorer
                </Typography>
                {getStatusChip('Verified')}
              </Box>
              
              <Box display="flex" alignItems="center" marginBottom={2}>
                <LockIcon color="primary" style={{ marginRight: 8 }} />
                <Typography variant="body1">
                  Liquidity Locked
                </Typography>
                {getStatusChip('Locked')}
              </Box>
              
              <Box display="flex" alignItems="center" marginBottom={2}>
                <SecurityIcon color="primary" style={{ marginRight: 8 }} />
                <Typography variant="body1">
                  Team Tokens Locked
                </Typography>
                {getStatusChip('Locked')}
              </Box>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Box display="flex" alignItems="center" marginBottom={2}>
                <VisibilityIcon color="warning" style={{ marginRight: 8 }} />
                <Typography variant="body1">
                  Audit Pending
                </Typography>
                {getStatusChip('Pending', true)}
              </Box>
              
              <Box display="flex" alignItems="center" marginBottom={2}>
                <SecurityIcon color="warning" style={{ marginRight: 8 }} />
                <Typography variant="body1">
                  KYC Pending
                </Typography>
                {getStatusChip('Pending', true)}
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Liquidity Locks */}
      <Card className={classes.card}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Liquidity Locks
          </Typography>
          
          <TableContainer component={Paper} className={classes.table}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Platform</TableCell>
                  <TableCell>Amount</TableCell>
                  <TableCell>Lock Duration</TableCell>
                  <TableCell>Unlock Date</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {liquidityLocks.map((lock, index) => (
                  <TableRow key={index}>
                    <TableCell>{lock.platform}</TableCell>
                    <TableCell>{lock.amount}</TableCell>
                    <TableCell>{lock.lockDuration}</TableCell>
                    <TableCell>{lock.unlockDate}</TableCell>
                    <TableCell>
                      {getStatusChip(lock.status)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Team Wallets */}
      <Card className={classes.card}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Team Wallets & Token Distribution
          </Typography>
          
          <TableContainer component={Paper} className={classes.table}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Wallet Address</TableCell>
                  <TableCell>Purpose</TableCell>
                  <TableCell>Tokens</TableCell>
                  <TableCell>Lock Duration</TableCell>
                  <TableCell>Unlock Date</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {teamWallets.map((wallet, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <Link 
                        href={`https://explorer.solana.com/address/${wallet.wallet}`}
                        target="_blank"
                        color="primary"
                      >
                        {wallet.wallet.slice(0, 8)}...{wallet.wallet.slice(-8)}
                      </Link>
                    </TableCell>
                    <TableCell>{wallet.purpose}</TableCell>
                    <TableCell>{wallet.tokens}</TableCell>
                    <TableCell>{wallet.lockDuration}</TableCell>
                    <TableCell>{wallet.unlockDate}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* External Links */}
      <Card className={classes.card}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Verify on External Platforms
          </Typography>
          
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <Button
                className={classes.button}
                fullWidth
                variant="contained"
                startIcon={<LinkIcon />}
                onClick={() => window.open(contractLinks.solanaExplorer, '_blank')}
              >
                Solana Explorer
              </Button>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <Button
                className={classes.button}
                fullWidth
                variant="contained"
                startIcon={<LinkIcon />}
                onClick={() => window.open(contractLinks.raydium, '_blank')}
              >
                Raydium
              </Button>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <Button
                className={classes.button}
                fullWidth
                variant="contained"
                startIcon={<LinkIcon />}
                onClick={() => window.open(contractLinks.birdeye, '_blank')}
              >
                Birdeye
              </Button>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <Button
                className={classes.button}
                fullWidth
                variant="contained"
                startIcon={<LinkIcon />}
                onClick={() => window.open(contractLinks.dexscreener, '_blank')}
              >
                DexScreener
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Detailed Information */}
      <Card className={classes.card}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Detailed Information
          </Typography>
          
          <Accordion className={classes.accordion}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} className={classes.accordionSummary}>
              <Typography>Token Contract Details</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body2" paragraph>
                <strong>Contract Address:</strong> 5ybhspwr2nPH4zTXq95QdE4CuoxmasDuJ1d1h74hmehb
              </Typography>
              <Typography variant="body2" paragraph>
                <strong>Total Supply:</strong> 1,000,000,000 LLAMA
              </Typography>
              <Typography variant="body2" paragraph>
                <strong>Decimals:</strong> 9
              </Typography>
              <Typography variant="body2" paragraph>
                <strong>Program ID:</strong> TokenkegQfeZyiNwAJbNbGKPFXCWuBvf9Ss623VQ5DA
              </Typography>
            </AccordionDetails>
          </Accordion>

          <Accordion className={classes.accordion}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} className={classes.accordionSummary}>
              <Typography>Security Measures</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body2" paragraph>
                • Liquidity locked for 1 year on multiple platforms
              </Typography>
              <Typography variant="body2" paragraph>
                • Team tokens locked for 6 months with gradual release
              </Typography>
              <Typography variant="body2" paragraph>
                • No mint function - supply is fixed
              </Typography>
              <Typography variant="body2" paragraph>
                • All transactions are public and verifiable
              </Typography>
              <Typography variant="body2" paragraph>
                • Contract code is open source and verified
              </Typography>
            </AccordionDetails>
          </Accordion>

          <Accordion className={classes.accordion}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} className={classes.accordionSummary}>
              <Typography>Risk Disclosures</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body2" paragraph>
                • Cryptocurrency investments are highly volatile
              </Typography>
              <Typography variant="body2" paragraph>
                • Past performance does not guarantee future results
              </Typography>
              <Typography variant="body2" paragraph>
                • Only invest what you can afford to lose
              </Typography>
              <Typography variant="body2" paragraph>
                • This is not financial advice
              </Typography>
            </AccordionDetails>
          </Accordion>
        </CardContent>
      </Card>

      {/* Last Updated */}
      <Alert severity="info" style={{ marginTop: 16 }}>
        <Typography variant="body2">
          Last updated: {new Date(auditData.lastUpdated).toLocaleDateString()}
        </Typography>
      </Alert>
      </Container>
    </Box>
  );
};

export default AuditTransparency; 